import { Group, Stack, Title } from "@mantine/core";
import { FormattedMessage, useIntl } from "react-intl";
import { useTRPC } from "../../../trpc";
import { useQuery } from "@tanstack/react-query";
import { EChartTimeseries } from "../../../components/EChartTimeseries";
import { endOfMonth } from "date-fns/endOfMonth";
import { startOfMonth } from "date-fns/startOfMonth";
import { useMemo } from "react";
import { UserCellRenderer } from "../../../utils/columns";
import type { ColDef } from "ag-grid-community";
import { AgGridTable } from "../../../components/AgGridTable";
import { useMemoizedBarSeries } from "../../../hooks/chart.js";

type Props = {
  selectedAccounts: string[];
  selectedUsers: string[];
};

export const AssistantUsageTab = ({ selectedAccounts, selectedUsers }: Props) => {
  const trpc = useTRPC();
  const intl = useIntl();

  const { data } = useQuery(
    trpc.getAccountsAssistantsUsage.queryOptions({
      accountIds: selectedAccounts.map(Number),
      userIds: selectedUsers.map(Number),
      from: startOfMonth(new Date()),
      to: endOfMonth(new Date()),
      interval: "day",
    }),
  );
  const { data: ranking } = useQuery(
    trpc.getAccountsAssistantUsageRanking.queryOptions({
      accountIds: selectedAccounts.map(Number),
      userIds: selectedUsers.map(Number),
      from: startOfMonth(new Date()),
      to: endOfMonth(new Date()),
    }),
  );

  type RankingRow = NonNullable<typeof ranking>["output"][number];

  const columnDefs: ColDef<RankingRow>[] = useMemo(() => [
    {
      headerName: intl.formatMessage({ defaultMessage: "Account", id: "column.account" }),
      valueGetter: params => params.data?.account.name,
    },
    {

      headerName: intl.formatMessage({ defaultMessage: "User", id: "column.user" }),
      valueGetter: params => params.data?.user,
      cellRenderer: UserCellRenderer,
    },
    {
      field: "total",
      headerName: intl.formatMessage({
        defaultMessage: "Total Output Tokens",
        id: "column.totalOutputTokens",
      }),
    },
  ], [intl]);

  const outputTokenSeries = useMemoizedBarSeries(data?.output);
  const inputTokenSeries = useMemoizedBarSeries(data?.input);

  return (
    <Stack gap="md" h="100%" p="md" mt="md">
      <Stack>
        <Title size="h4">
          <FormattedMessage defaultMessage="Output Tokens" id="label.outputTokens" />
        </Title>
        <Group h="320px">
          <EChartTimeseries
            series={outputTokenSeries}
            xLabel="Date"
            yLabel="Tokens"
          />
        </Group>
      </Stack>
      <Stack>
        <Title size="h4">
          <FormattedMessage defaultMessage="Input Tokens" id="label.inputTokens" />
        </Title>
        <Group h="320px">
          <EChartTimeseries
            series={inputTokenSeries}
            xLabel="Date"
            yLabel="Tokens"
          />
        </Group>
      </Stack>
      <Stack>
        <Title size="h4">
          <FormattedMessage defaultMessage="Usage Ranking" id="label.usageRanking" />
        </Title>
        {ranking?.output && ranking.output.length > 0 && (
          <AgGridTable
            rowData={ranking.output}
            columnDefs={columnDefs}
            domLayout="autoHeight"
            autoSizeStrategy={{
              type: "fitGridWidth",
              defaultMinWidth: 100,
            }}
            suppressHorizontalScroll={false}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            paginationPageSize={20}
            pagination={true}
          />
        )}
      </Stack>
    </Stack>
  );
};
